/**
 * The ONE tank fill-rate rule (Phase 13 task 13.1, DEC-348). The operator portal reads it now;
 * the dispatcher Tanks view (13.3) and the auto-haul sweeper (13.4) are meant to call the same
 * function, so the three can never disagree about when a tank is full.
 *
 * Readings are a tank's haul captures in time order, each with the level before and after the
 * haul. BEFORE_AFTER captures record both. SINGLE_GAUGE captures record neither, so
 * toTankLevelReadings ESTIMATES them (DEC-351, reversing DEC-024's no-strapping rule):
 *     arriving level = top gauge in inches × the tank's bbl_per_inch
 *     leaving level  = arriving level − (water + oil hauled)
 * An estimated reading is flagged, and the hauler's tank_estimates_enabled switch drops
 * estimates entirely (migration 0173).
 *
 * Interval: from one haul's AFTER level to the next haul's BEFORE level. The gain over that gap
 * is what the well produced while nobody was hauling. A capture with no AFTER breaks the chain,
 * because the level it left the tank at is unknown.
 *
 * An interval is skipped when it is shorter than an hour (two tickets for one visit, or a clock
 * glitch, would give an absurd rate) or its gain is negative (a missed haul or a bad entry).
 *
 * DEC-370 (amends DEC-348 for dense readings): a reading inside the hour that does not lower the
 * level (before = after, no fall since the anchor) does not restart the interval. The interval
 * keeps its start until an hour has passed, so readings every few minutes give the rate of their
 * hourly samples instead of no rate at all. A haul, a fall in level or a missing level inside the
 * hour resets the anchor as before.
 *
 * Fill rate = the mean of the most recent FILL_RATE_WINDOW valid intervals. Fewer than
 * MIN_FILL_RATE_INTERVALS means "not enough history": the rate and prediction are null, never a
 * guess.
 *
 * Computed on read: nothing is stored, so a value can never be stale (DEC-348).
 */

export const FILL_RATE_WINDOW = 5;
export const MIN_FILL_RATE_INTERVALS = 2;

// ── Display only: "Not enough history" ───────────────────────────────────────────────────────
//
// The 2026-10-20 pumper proof: three loads an hour apart read as 5,634 BBL/day with a "full" time
// already past. The rule above is right to keep frequent readings (pumper entries, future MQTT),
// so the VIEWS refuse to show a rate or a projection from readings that cover less than
// MIN_HISTORY_SPAN_HOURS in all. The mobile pumper Levels and the dispatcher Tanks tab both call
// these, so they cannot drift. computeTankFillRate and the auto-haul sweeper never do.

/** The least total time a tank's readings must cover before a view shows a rate or a full time. */
export const MIN_HISTORY_SPAN_HOURS = 6;

/** First to last of the given reading times, in hours; null with no usable time. */
export function historySpanHours(times: Iterable<string>): number | null {
  let min = Infinity;
  let max = -Infinity;
  for (const t of times) {
    const ms = Date.parse(t);
    if (!Number.isFinite(ms)) continue;
    if (ms < min) min = ms;
    if (ms > max) max = ms;
  }
  return min === Infinity ? null : (max - min) / (60 * 60 * 1000);
}

/** True when the readings are known and cover too little time to project from. Unknown = false. */
export function isHistoryTooShort(spanHours: number | null | undefined): boolean {
  return spanHours !== null && spanHours !== undefined && spanHours < MIN_HISTORY_SPAN_HOURS;
}

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export interface TankLevelReading {
  /** When the haul was captured (ISO datetime). */
  at: string;
  /** BBL before the haul, as a numeric string or number; null when not captured. */
  beforeBbl: string | number | null;
  /** BBL after the haul; null when not captured. */
  afterBbl: string | number | null;
}

export interface TankFillRate {
  /** Mean fill rate in BBL per day, 2 dp; null under MIN_FILL_RATE_INTERVALS. */
  fillRateBpd: number | null;
  /** Valid intervals the rate is based on (at most FILL_RATE_WINDOW). */
  intervalCount: number;
  /**
   * When the tank reaches capacity, projected from the latest AFTER level at fillRateBpd.
   * Null without a rate, a positive rate, a capacity or a known level. A tank already at or
   * over capacity reports the time of that reading.
   */
  predictedFullAt: string | null;
}

function toNumber(v: string | number | null): number | null {
  if (v === null) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Readings must be in ascending time order, as the portal reader returns them. */
export function computeTankFillRate(
  readings: readonly TankLevelReading[],
  capacityBbl: string | number | null,
): TankFillRate {
  const rates: number[] = [];
  let lastAfter: { bbl: number; ms: number } | null = null;

  for (const r of readings) {
    const ms = Date.parse(r.at);
    if (!Number.isFinite(ms)) continue;
    const before = toNumber(r.beforeBbl);
    const after = toNumber(r.afterBbl);

    if (lastAfter !== null && before !== null) {
      const elapsed = ms - lastAfter.ms;
      const gain = before - lastAfter.bbl;
      // DEC-370: a reading inside the hour that does not lower the level keeps the anchor, so
      // readings every few minutes give the same intervals as their hourly samples.
      if (elapsed < MS_PER_HOUR && after !== null && after === before && gain >= 0) continue;
      if (elapsed >= MS_PER_HOUR && gain >= 0) {
        rates.push(gain / (elapsed / MS_PER_DAY));
      }
    }
    lastAfter = after !== null ? { bbl: after, ms } : null;
  }

  const recent = rates.slice(-FILL_RATE_WINDOW);
  if (recent.length < MIN_FILL_RATE_INTERVALS) {
    return { fillRateBpd: null, intervalCount: recent.length, predictedFullAt: null };
  }

  const mean = recent.reduce((sum, x) => sum + x, 0) / recent.length;
  const fillRateBpd = Math.round(mean * 100) / 100;

  let predictedFullAt: string | null = null;
  const capacity = toNumber(capacityBbl);
  const latest = latestAfter(readings);
  if (capacity !== null && capacity > 0 && latest !== null && mean > 0) {
    const remaining = Math.max(capacity - latest.bbl, 0);
    predictedFullAt = new Date(latest.ms + (remaining / mean) * MS_PER_DAY).toISOString();
  }

  return { fillRateBpd, intervalCount: recent.length, predictedFullAt };
}

function latestAfter(readings: readonly TankLevelReading[]): { bbl: number; ms: number } | null {
  for (let i = readings.length - 1; i >= 0; i--) {
    const r = readings[i]!;
    const after = toNumber(r.afterBbl);
    const ms = Date.parse(r.at);
    if (after !== null && Number.isFinite(ms)) return { bbl: after, ms };
  }
  return null;
}

// ── DEC-351 — levels from raw capture rows, and the estimated current volume ─────────────────

/** One haul_capture_tanks row as the readers return it (operator_portal_tank_gauge_readings). */
export interface TankCaptureRow {
  at: string;
  beforeBbl: string | number | null;
  afterBbl: string | number | null;
  topGaugeFeet: number | null;
  topGaugeInches: number | null;
  /** water + oil hauled; null when neither was captured. */
  hauledBbl: string | number | null;
}

export interface TankLevelReadingWithSource extends TankLevelReading {
  /** True when the levels were derived from the gauge (DEC-351), not recorded. */
  estimated: boolean;
}

/**
 * A row with a recorded level (either side) is used as recorded. Otherwise, when estimates are
 * on and the tank has a positive bbl_per_inch and a top gauge, the levels are estimated; a row
 * with no hauled volume gets an arriving level only. Anything else is dropped. With estimates
 * off, only recorded rows remain.
 */
export function toTankLevelReadings(
  rows: readonly TankCaptureRow[],
  bblPerInch: string | number | null,
  estimatesEnabled: boolean,
): TankLevelReadingWithSource[] {
  const bpi = toNumber(bblPerInch);
  const out: TankLevelReadingWithSource[] = [];
  for (const r of rows) {
    if (toNumber(r.beforeBbl) !== null || toNumber(r.afterBbl) !== null) {
      out.push({ at: r.at, beforeBbl: r.beforeBbl, afterBbl: r.afterBbl, estimated: false });
      continue;
    }
    if (!estimatesEnabled || bpi === null || bpi <= 0 || r.topGaugeFeet === null) continue;
    const inches = r.topGaugeFeet * 12 + (r.topGaugeInches ?? 0);
    const arriving = round2(inches * bpi);
    const hauled = toNumber(r.hauledBbl);
    const leaving = hauled === null ? null : round2(Math.max(arriving - hauled, 0));
    out.push({ at: r.at, beforeBbl: arriving, afterBbl: leaving, estimated: true });
  }
  return out;
}

/** A hand-entered tank reading (tank_readings, migration 0233): one level at one time. */
export interface TankHandReading {
  at: string;
  /** Null for a reading given in inches while the tank's bbl per inch is unknown (0245). */
  levelBbl: string | number | null;
}

/**
 * Phase 13.2 — hand readings as fill-rate readings. A reading is a point with no haul, so the
 * level before and after it are the same: the gain since the previous haul (or reading) counts,
 * and the next interval starts from it. Recorded, never estimated. Merge with the capture
 * readings with mergeTankLevelReadings, which keeps the time order computeTankFillRate needs.
 */
export function toHandLevelReadings(
  readings: readonly TankHandReading[],
): TankLevelReadingWithSource[] {
  const out: TankLevelReadingWithSource[] = [];
  for (const r of readings) {
    if (toNumber(r.levelBbl) === null) continue;
    out.push({ at: r.at, beforeBbl: r.levelBbl, afterBbl: r.levelBbl, estimated: false });
  }
  return out;
}

/** Both sources in ascending time order (stable: a capture before a reading at the same time). */
export function mergeTankLevelReadings(
  captures: readonly TankLevelReadingWithSource[],
  hand: readonly TankLevelReadingWithSource[],
): TankLevelReadingWithSource[] {
  return [...captures, ...hand]
    .map((r, i) => ({ r, i, ms: Date.parse(r.at) }))
    .sort((x, y) => (x.ms === y.ms ? x.i - y.i : x.ms - y.ms))
    .map((x) => x.r);
}

/**
 * The tank's level now: the latest after level carried forward at the fill rate, capped at
 * capacity. Without a rate it is the latest after level itself. Null with no after level.
 * Always an estimate, and only shown when the hauler's switch is on.
 */
export function estimateCurrentTankBbl(
  readings: readonly TankLevelReading[],
  fillRateBpd: number | null,
  capacityBbl: string | number | null,
  now: number = Date.now(),
): number | null {
  const latest = latestAfter(readings);
  if (latest === null) return null;
  let level = latest.bbl;
  if (fillRateBpd !== null && fillRateBpd > 0 && now > latest.ms) {
    level += fillRateBpd * ((now - latest.ms) / MS_PER_DAY);
  }
  const capacity = toNumber(capacityBbl);
  if (capacity !== null && capacity > 0) level = Math.min(level, capacity);
  return round2(level);
}

/**
 * Phase 13.4a — when the tank reaches its threshold (thresholdPercent of capacity), projected from
 * the latest after level at the fill rate. A tank already at or over it reports the time of that
 * level. Null without a threshold, a capacity, a level, or a positive rate (below the threshold).
 * The Tanks tab shows it; 13.4b's sweeper acts on it, so the two cannot disagree. It takes the
 * fill rate as reported (fillRateBpd, 2 dp), so at a 100% threshold it can differ from
 * predictedFullAt (which uses the unrounded mean) by that rounding only.
 */
export function predictThresholdAt(
  readings: readonly TankLevelReading[],
  fillRateBpd: number | null,
  capacityBbl: string | number | null,
  thresholdPercent: string | number | null,
): string | null {
  const capacity = toNumber(capacityBbl);
  const pct = toNumber(thresholdPercent);
  const latest = latestAfter(readings);
  if (capacity === null || capacity <= 0 || pct === null || pct <= 0 || latest === null) {
    return null;
  }
  const thresholdBbl = (capacity * pct) / 100;
  if (latest.bbl >= thresholdBbl) return new Date(latest.ms).toISOString();
  if (fillRateBpd === null || fillRateBpd <= 0) return null;
  return new Date(
    latest.ms + ((thresholdBbl - latest.bbl) / fillRateBpd) * MS_PER_DAY,
  ).toISOString();
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
